import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { area, bbox, booleanIntersects, featureCollection, intersect, polygon } from '@turf/turf';
import { wgsToLV95 } from 'domain/utils/coordinates';
import {
  GetAddressByPointRequest,
  GetBuildingLawEgridsRequest,
  SearchAddressRequest,
  SearchPlotNrRequest,
} from 'state/services/geoAdmin/types/request';
import {
  GetAddressByPointResponse,
  GetBuildingLawEgridsResponse,
  SearchAddressResponse,
  SearchByPlotNumberResponse,
} from 'state/services/geoAdmin/types/response';
import {
  getAddressByPointSchema,
  getBuildingLawEgridsSchema,
  searchAddressSchema,
  searchByPlotNumberSchema,
} from 'state/services/geoAdmin/types/schema';
import { areMultiPolygonCoordinates } from 'utils/geojson';

export const geoAdminApi = createApi({
  reducerPath: 'geoAdminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://api3.geo.admin.ch/rest/services',
  }),
  endpoints: (builder) => ({
    searchAddress: builder.query<SearchAddressResponse, SearchAddressRequest>({
      query: ({ address }) =>
        `/api/SearchServer?sr=2056&searchText=${encodeURIComponent(address)}&lang=de&type=locations`,
      responseSchema: searchAddressSchema,
    }),
    searchByPlotNumber: builder.query<SearchByPlotNumberResponse, SearchPlotNrRequest>({
      query: ({ plotNr, canton }) =>
        `/api/SearchServer?sr=2056&searchText=${plotNr},${canton}&lang=de&type=locations&origins=parcel,gg25`,
      responseSchema: searchByPlotNumberSchema,
    }),
    getAddressByPoint: builder.query<GetAddressByPointResponse, GetAddressByPointRequest>({
      query: ({ lng, lat }) => {
        const { x, y } = wgsToLV95({ lng, lat });

        return `/api/MapServer/identify?mapExtent=0,0,100,100&imageDisplay=100,100,100&tolerance=50&sr=2056&geometryType=esriGeometryPoint&geometry=${x},${y}&layers=all:ch.bfs.gebaeude_wohnungs_register&returnGeometry=true`;
      },
      responseSchema: getAddressByPointSchema,
    }),
    getBuildingLawEgrids: builder.query<GetBuildingLawEgridsResponse, GetBuildingLawEgridsRequest>({
      query: (request) => {
        const plotPolygon = polygon([request.polygon]);
        const [minX, minY, maxX, maxY] = bbox(plotPolygon);
        const geometry = `${minX},${minY},${maxX},${maxY}`;

        return `/api/MapServer/identify?layers=all:ch.swisstopo-vd.stand-oerebkataster&sr=2056&geometry=${geometry}&mapExtent=0,0,100,100&imageDisplay=100,100,100&geometryFormat=geojson&geometryType=esriGeometryEnvelope&limit=100&tolerance=1&returnGeometry=true`;
      },
      responseSchema: getBuildingLawEgridsSchema,
      transformResponse: (response: GetBuildingLawEgridsResponse, _, arg): GetBuildingLawEgridsResponse => {
        if (!response?.results) return { results: [] };

        const plotPolygon = polygon([arg.polygon]);
        const plotArea = area(plotPolygon);
        const minOverlapPercentage = 0.01; // 1% minimum overlap
        const minOverlapArea = plotArea * minOverlapPercentage;

        const results = response.results.filter((feature) => {
          const isBuildingLaw = feature.properties?.realestate_type === 'SelbstRecht.Baurecht';
          if (!isBuildingLaw) return false;

          try {
            const featurePolygon = areMultiPolygonCoordinates(feature.geometry.coordinates)
              ? polygon(feature.geometry.coordinates[0])
              : polygon(feature.geometry.coordinates);

            if (!booleanIntersects(plotPolygon, featurePolygon)) return false;

            const featuresToIntersect = featureCollection([plotPolygon, featurePolygon]);

            const intersection = intersect(featuresToIntersect);
            if (!intersection) return false;

            const intersectionArea = area(intersection);
            return intersectionArea >= minOverlapArea;
          } catch (_e) {
            return false;
          }
        });

        return { results };
      },
    }),
  }),
});

export const {
  useSearchAddressQuery,
  useSearchByPlotNumberQuery,
  useGetAddressByPointQuery,
  useGetBuildingLawEgridsQuery,
} = geoAdminApi;
